/**
 * Supervisor control actions — the REAL transports, with no demo seam.
 *
 * WHY THIS IS A SEPARATE MODULE FROM `./actions.ts`
 * ------------------------------------------------
 * `./actions.ts` short-circuits every action when `DEMO_MODE` is on: call
 * controls pop a fake "applied to call" toast and agent controls move a
 * simulated agent. That is correct for the demo dashboard, and fatal for a page
 * whose entire purpose is real data — with the flag on, a live page importing
 * that hook would show working buttons that reached no server at all.
 *
 * This module does not import `./demo-mode.js`, so it cannot be affected by the
 * flag no matter what it is set to. `./actions.ts` delegates its non-demo
 * branches here, so the two pages share one implementation of the transports
 * rather than drifting apart.
 *
 * TWO TRANSPORTS, and which one an action uses is not arbitrary:
 *
 *   REST   Call-scoped control (listen / whisper / coach / barge / park /
 *          unpark / conference / hold / unhold). `POST /supervisor/{action}/:callId`
 *          carries per-action permission gating, idempotency-key handling and
 *          audit logging; the gateway has no equivalent surface.
 *
 *   SOCKET Agent-scoped control (force pause / force logout). These exist only
 *          as `supervisor:force_*` gateway events, which publish onto
 *          `supervisor.action.<tenant>` for the worker to apply.
 *
 * ACCEPTANCE IS NOT COMPLETION. Both transports are asynchronous by design: the
 * worker applies the change and the result arrives as a state transition on the
 * live feed, or as a `supervisor:alert` if it failed (forwarded by ws-gateway's
 * supervisor-bridge). The UI must NOT optimistically mutate an agent row — if
 * the FSM rejects the transition, an optimistic update shows a lie until the
 * next push corrects it. The toasts here deliberately say "requested".
 */
import { useCallback } from 'react'
import { getSocket } from '../../../socket/client.js'
import { useAuthStore } from '../../../store/authStore.js'
import { useUiStore } from '../../../store/uiStore.js'

export type CallAction =
  | 'listen'
  | 'whisper'
  | 'coach'
  | 'barge'
  | 'park'
  | 'unpark'
  | 'conference'
  | 'hold'
  | 'unhold'

export const ACTION_LABEL: Record<CallAction, string> = {
  listen: 'Listen',
  whisper: 'Whisper',
  coach: 'Coach',
  barge: 'Barge',
  park: 'Park',
  unpark: 'Unpark',
  conference: 'Conference',
  hold: 'Hold',
  unhold: 'Unhold',
}

/**
 * Actions that place the supervisor on a live call, and therefore need their
 * own device to ring. Called out in the toast because the audio arrives on the
 * supervisor's softphone, not in the browser tab they clicked in — without
 * this, a working listen looks like a broken one for the ~2s before the phone
 * rings.
 */
const RINGS_SUPERVISOR: ReadonlySet<CallAction> = new Set<CallAction>([
  'listen',
  'whisper',
  'coach',
  'barge',
])

export interface SupervisorActions {
  invokeCallAction: (action: CallAction, callId: string) => void
  forcePause: (agentId: string, agentName: string) => void
  forceLogout: (agentId: string, agentName: string) => void
}

export function useLiveSupervisorActions(): SupervisorActions {
  const csrfToken = useAuthStore((s) => s.csrfToken)
  const addNotification = useUiStore((s) => s.addNotification)

  const invokeCallAction = useCallback(
    (action: CallAction, callId: string) => {
      void (async () => {
        try {
          const res = await fetch(`/api/supervisor/${action}/${callId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
            },
            credentials: 'include',
            body: '{}',
          })
          if (res.ok) {
            addNotification(
              RINGS_SUPERVISOR.has(action)
                ? `${ACTION_LABEL[action]} requested — answer your softphone`
                : `${ACTION_LABEL[action]} requested`,
              'info',
            )
            return
          }
          // 403 is the common, actionable case — the supervisor permission
          // split means a role can hold `calls:listen` but not `calls:barge`,
          // and "failed (403)" does not tell them that.
          addNotification(
            res.status === 403
              ? `You do not have permission to ${ACTION_LABEL[action].toLowerCase()} on this call.`
              : `${ACTION_LABEL[action]} failed (${res.status})`,
            'error',
          )
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'Unknown error'
          addNotification(`${ACTION_LABEL[action]} failed: ${msg}`, 'error')
        }
      })()
    },
    [csrfToken, addNotification],
  )

  // `agentId` here is a USER id. The worker's presence keys and the
  // force-logout session kick are both built from users.id — see
  // SupervisorAgentSummary.userId and the callers in the agents panel.
  const forcePause = useCallback(
    (agentId: string, agentName: string) => {
      getSocket().emit('supervisor:force_pause', { agentId })
      addNotification(`Pause requested for ${agentName}`, 'info')
    },
    [addNotification],
  )

  const forceLogout = useCallback(
    (agentId: string, agentName: string) => {
      getSocket().emit('supervisor:force_logout', { agentId })
      addNotification(`Logout requested for ${agentName}`, 'info')
    },
    [addNotification],
  )

  return { invokeCallAction, forcePause, forceLogout }
}
